/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from "react";
import {
  Divider,
  Layout,
  Typography,
  Table,
  Tag,
  Space,
  Row,
  Spin,
} from "antd";
import { getDistributionTransactions } from "../../../api/distributionTransactions";
import { getDistributionPayouts } from "../../../api/distributionProject";
import functions from "../../../components/utils/functions";
import { netPayoutFromRevenue } from "../../../components/utils/youtubePlatform";

const { Content } = Layout;

const { Title, Text } = Typography;

const STATUS_TAGS = {
  pending: { color: "warning", text: "Pending" },
  success: { color: "success", text: "Success" },
  initiated: { color: "processing", text: "Initiated" },
  failed: { color: "error", text: "Failed" },
};

const netPayoutAmount = (revenue, platformId, platformName) =>
  `₹${functions.formatIndianNumber(
    netPayoutFromRevenue(revenue, platformId, platformName),
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )}`;

const Payout = ({ effectiveUid, projects }) => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAggregatedPayouts = useCallback(async () => {
    if (!effectiveUid) return;
    setLoading(true);
    try {
      const { payouts: list } = await getDistributionPayouts(effectiveUid);
      setPayouts(list);
    } catch (_) {
      setPayouts([]);
    } finally {
      setLoading(false);
    }
  }, [effectiveUid]);

  const fetchLegacyPayouts = useCallback(async () => {
    if (!projects || projects.length === 0) {
      setPayouts([]);
      return;
    }
    setLoading(true);
    try {
      const projectIds = projects.map((doc) => doc.id);
      const projectTitleById = projects.reduce((acc, doc) => {
        const data = typeof doc.data === "function" ? doc.data() : doc.data?.();
        return {
          ...acc,
          [doc.id]: (data && (data.title || data.uid)) || doc.id,
        };
      }, {});
      const transactions = await getDistributionTransactions(projectIds);
      const byKey = {};
      transactions
        .filter((row) => Number(row.revenue || 0) > 0)
        .forEach((row) => {
          const key = `${row.distribution_project_id}-${row.platform_id}-${
            row.revenue_status || "pending"
          }`;
          if (!byKey[key]) {
            byKey[key] = {
              project_id: row.distribution_project_id,
              project_title:
                projectTitleById[row.distribution_project_id] ||
                row.distribution_project_id,
              platform_id: row.platform_id,
              status: row.revenue_status || "pending",
              period_start: row.month_date,
              period_end: row.month_date,
              total_revenue: 0,
            };
          }
          const r = byKey[key];
          r.total_revenue += Number(row.revenue) || 0;
          if (
            !r.period_start ||
            (row.month_date && row.month_date < r.period_start)
          ) {
            r.period_start = row.month_date;
          }
          if (
            !r.period_end ||
            (row.month_date && row.month_date > r.period_end)
          ) {
            r.period_end = row.month_date;
          }
        });
      const list = Object.values(byKey).map((r) => {
        const start = r.period_start ? new Date(r.period_start) : null;
        const end = r.period_end ? new Date(r.period_end) : null;
        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const fmt = (d) =>
          d ? `${months[d.getMonth()]} ${d.getFullYear()}` : "";
        return {
          ...r,
          period_label: start && end ? `${fmt(start)} – ${fmt(end)}` : "",
        };
      });
      setPayouts(
        list.sort((a, b) =>
          (b.period_end || "") > (a.period_end || "") ? 1 : -1
        )
      );
    } catch (_) {
      setPayouts([]);
    } finally {
      setLoading(false);
    }
  }, [projects]);

  useEffect(() => {
    if (effectiveUid) {
      fetchAggregatedPayouts();
    } else {
      fetchLegacyPayouts();
    }
  }, [effectiveUid, fetchAggregatedPayouts, fetchLegacyPayouts]);

  const columns = [
    {
      title: "Project",
      dataIndex: "project_title",
      key: "project_title",
      width: 160,
    },
    {
      title: "Platform",
      dataIndex: "platform_name",
      key: "platform_name",
      width: 140,
      render: (name, row) => name || row.platform_id,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) => {
        const info = STATUS_TAGS[status] || STATUS_TAGS.pending;
        return <Tag color={info.color}>{info.text}</Tag>;
      },
    },
    {
      title: "Period",
      dataIndex: "period_label",
      key: "period_label",
      width: 200,
    },
    {
      title: "Net Payout",
      key: "total_revenue",
      width: 120,
      render: (_, r) =>
        netPayoutAmount(r.total_revenue, r.platform_id, r.platform_name),
    },
  ];

  return (
    <Content
      className="vc-panel"
      style={{
        padding: 24,
        margin: 0,
      }}
    >
      <Title level={4}>Payouts</Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        Payouts will be released automatically when project payout per platform
        exceeds ₹1,000, subject to payment release from the platform.
      </Text>
      <Divider />
      {loading && payouts.length === 0 ? (
        <div className="vc-spin-wrap">
          <Spin tip="Loading payouts…" />
        </div>
      ) : payouts.length > 0 ? (
        <>
          <Table
            dataSource={payouts}
            columns={columns}
            rowKey={(r) => `${r.project_id}-${r.platform_id}-${r.status}`}
            loading={loading}
            pagination={false}
            scroll={{ x: 900 }}
          />
          <Space
            direction="vertical"
            style={{ textAlign: "left", marginTop: 16 }}
          >
            <Row>
              <Tag color="warning">Pending</Tag> — Payment not yet received from
              the platform.
            </Row>
            <Row>
              <Tag color="processing">Initiated</Tag> — Payment received from
              the platform and transfer initiated.
            </Row>
            <Row>
              <Tag color="success">Success</Tag> — Payment transferred to your
              account.
            </Row>
            <Row>
              <Tag color="error">Failed</Tag> — Transfer failed. Contact
              support.
            </Row>
          </Space>
        </>
      ) : (
        <span>You do not have any payouts yet.</span>
      )}
    </Content>
  );
};

export default Payout;
